<template>
  <div class="markdown-body" v-html="sanitizedHtml" />
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { marked } from 'marked'
import DOMPurify from 'dompurify'

const props = defineProps<{
  text: string | null
}>()

const sanitizedHtml = computed(() => {
  if (!props.text) return ''

  // Parse markdown
  const rawHtml = marked(props.text, {
    breaks: true,
    gfm: true,
  })

  // Sanitize HTML to prevent XSS
  return DOMPurify.sanitize(rawHtml as string)
})
</script>

<style>
.markdown-body {
  font-size: 15px;
  line-height: 1.7;
  color: var(--app-text-primary);
  word-break: break-word;
}

.markdown-body :deep(p) {
  margin: 0 0 10px 0;
}

.markdown-body :deep(p:last-child) {
  margin-bottom: 0;
}

.markdown-body :deep(strong) {
  font-weight: 700;
  color: var(--app-text-primary);
}

.markdown-body :deep(em) {
  font-style: italic;
}

.markdown-body :deep(del) {
  text-decoration: line-through;
  color: var(--app-text-secondary);
}

.markdown-body :deep(code) {
  background: var(--app-bg-muted);
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'SF Mono', Monaco, monospace;
  font-size: 13px;
  color: #f56c6c;
}

.markdown-body :deep(pre) {
  background: var(--app-bg-muted);
  padding: 12px;
  border-radius: 6px;
  overflow-x: auto;
  margin: 10px 0;
}

.markdown-body :deep(pre code) {
  background: none;
  padding: 0;
  color: var(--app-text-regular);
}

.markdown-body :deep(a) {
  color: #409eff;
  text-decoration: none;
  font-weight: 600;
}

.markdown-body :deep(a:hover) {
  text-decoration: underline;
}

.markdown-body :deep(ul),
.markdown-body :deep(ol) {
  margin: 8px 0;
  padding-left: 24px;
}

.markdown-body :deep(li) {
  margin: 4px 0;
}

.markdown-body :deep(blockquote) {
  margin: 10px 0;
  padding: 8px 16px;
  border-left: 4px solid #409eff;
  background: var(--app-info-soft);
  color: var(--app-text-regular);
}

.markdown-body :deep(h1),
.markdown-body :deep(h2),
.markdown-body :deep(h3),
.markdown-body :deep(h4),
.markdown-body :deep(h5),
.markdown-body :deep(h6) {
  margin: 16px 0 8px 0;
  font-weight: 700;
  color: var(--app-text-primary);
}

.markdown-body :deep(h1) { font-size: 22px; }
.markdown-body :deep(h2) { font-size: 20px; }
.markdown-body :deep(h3) { font-size: 18px; }
.markdown-body :deep(h4) { font-size: 16px; }
.markdown-body :deep(h5),
.markdown-body :deep(h6) { font-size: 15px; }

.markdown-body :deep(hr) {
  border: none;
  border-top: 1px solid var(--app-border-color);
  margin: 16px 0;
}

.markdown-body :deep(table) {
  border-collapse: collapse;
  width: 100%;
  margin: 10px 0;
}

.markdown-body :deep(th),
.markdown-body :deep(td) {
  border: 1px solid var(--app-border-color);
  padding: 8px 12px;
  text-align: left;
}

.markdown-body :deep(th) {
  background: var(--app-bg-muted);
  font-weight: 700;
}
</style>
